import { useState, useEffect, useCallback } from "react";
import { useSearchParams } from "react-router";
import { toast } from "sonner";
import { useDataAdapter } from "@shared-core/data/DataAdapterContext";
import type {
  AdminUserItem,
  AdminUserListOptions,
  PossibleLeaverRow,
} from "@shared-core/data/adapter";
import { ApiError } from "@shared-core/data/api/errors";
import { BUSINESS_ROLES, roleLabel } from "@shared-core/domain/roleLabels";
import { useDebouncedValue } from "@shared-core/domain/useDebouncedValue";
import { useAuth } from "../app/auth/hooks";
import { ListPagination } from "../app/components/patterns/ListPagination";
import { Card } from "../app/components/primitives/card";
import { Badge } from "../app/components/primitives/badge";
import { Button } from "../app/components/primitives/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../app/components/primitives/dialog";
import {
  Users,
  Search,
  Mail,
  Shield,
  KeyRound,
  Database,
  Merge,
  AlertTriangle,
  UserMinus,
  UserCheck,
} from "lucide-react";
import { currentTime } from "@shared-core/domain/current-time";

function formatShortDate(iso: string): string {
  return new Date(iso).toLocaleDateString(undefined, {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

const PAGE_SIZE = 25;

const ROLE_COLORS: Record<string, string> = {
  admin: "bg-red-100 text-red-800",
  manager: "bg-purple-100 text-purple-800",
  sales_rep: "bg-blue-100 text-blue-800",
  csr: "bg-gray-100 text-gray-800",
  engineer: "bg-teal-100 text-teal-800",
  commercial: "bg-indigo-100 text-indigo-800",
  stock_control: "bg-orange-100 text-orange-800",
  pending: "bg-yellow-100 text-yellow-800",
};

function RoleBadge({ role }: { role: string }) {
  const label = roleLabel(role);
  const colors = ROLE_COLORS[role] ?? "bg-gray-100 text-gray-800";
  return (
    <span
      className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${colors}`}
    >
      {label}
    </span>
  );
}

function IdentityBadge({ hasLoggedIn }: { hasLoggedIn: boolean }) {
  return hasLoggedIn ? (
    <span className="inline-flex items-center gap-1 text-xs text-green-700">
      <KeyRound className="h-3 w-3" /> Entra
    </span>
  ) : (
    <span className="inline-flex items-center gap-1 text-xs text-amber-600">
      <Database className="h-3 w-3" /> Tharstern
    </span>
  );
}

function MergeDialog({
  source,
  entraUsers,
  merging,
  error,
  onConfirm,
  onCancel,
}: {
  source: AdminUserItem;
  entraUsers: AdminUserItem[];
  merging: boolean;
  error: string | null;
  onConfirm: (targetId: string) => void;
  onCancel: () => void;
}) {
  const [search, setSearch] = useState("");
  const [selectedTargetId, setSelectedTargetId] = useState<string | null>(null);

  // Sort same-name users to top, then filter by search
  const candidates = entraUsers
    .filter((u) => u.id !== source.id)
    .sort((a, b) => {
      const aMatch = a.name.toLowerCase() === source.name.toLowerCase() ? 0 : 1;
      const bMatch = b.name.toLowerCase() === source.name.toLowerCase() ? 0 : 1;
      if (aMatch !== bMatch) return aMatch - bMatch;
      return a.name.localeCompare(b.name);
    })
    .filter((u) => {
      if (!search) return true;
      const q = search.toLowerCase();
      return (
        u.name.toLowerCase().includes(q) ||
        u.primary_email.toLowerCase().includes(q)
      );
    });

  const selectedTarget = candidates.find((u) => u.id === selectedTargetId);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
      onClick={onCancel}
    >
      <div
        className="bg-background rounded-lg shadow-lg max-w-lg w-full mx-4 p-6 space-y-5"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 className="text-lg font-semibold flex items-center gap-2">
          <Merge className="h-5 w-5" /> Merge user
        </h2>

        {/* Source user */}
        <div className="space-y-1">
          <p className="text-sm font-medium text-muted-foreground">
            Absorb this Tharstern user:
          </p>
          <div className="border rounded-md p-3 bg-muted/30">
            <p className="font-medium">{source.name}</p>
            <p className="text-sm text-muted-foreground">
              {source.primary_email}
            </p>
            <div className="flex items-center gap-3 mt-1 text-xs text-muted-foreground">
              <span>Role: {roleLabel(source.role)}</span>
              {source.portfolios.length > 0 && (
                <span>
                  Portfolios: {source.portfolios.map((p) => p.name).join(", ")}
                </span>
              )}
            </div>
          </div>
        </div>

        {/* Arrow */}
        <div className="flex justify-center text-muted-foreground">
          <span className="text-lg">↓</span>
        </div>

        {/* Target picker */}
        <div className="space-y-2">
          <p className="text-sm font-medium text-muted-foreground">
            Into this Entra user:
          </p>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <input
              type="text"
              placeholder="Search for target user..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-9 pr-3 py-2 border border-input rounded-md bg-background text-sm focus:outline-none focus:ring-2 focus:ring-ring"
            />
          </div>
          <div className="max-h-48 overflow-y-auto border rounded-md divide-y">
            {candidates.length === 0 ? (
              <p className="px-3 py-4 text-sm text-muted-foreground text-center">
                No Entra users found
              </p>
            ) : (
              candidates.slice(0, 20).map((u) => (
                <button
                  key={u.id}
                  type="button"
                  onClick={() => setSelectedTargetId(u.id)}
                  className={`w-full text-left px-3 py-2 text-sm hover:bg-muted/50 transition-colors ${
                    selectedTargetId === u.id ? "bg-muted" : ""
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <span className="font-medium">{u.name}</span>
                      {u.name.toLowerCase() === source.name.toLowerCase() && (
                        <span className="ml-2 text-xs text-green-600 font-medium">
                          name match
                        </span>
                      )}
                    </div>
                    <KeyRound className="h-3 w-3 text-green-700" />
                  </div>
                  <p className="text-xs text-muted-foreground">
                    {u.primary_email}
                  </p>
                </button>
              ))
            )}
          </div>
        </div>

        {/* What will happen */}
        {selectedTarget && (
          <div className="bg-muted/30 border rounded-md p-3 text-sm space-y-1">
            <p className="font-medium">What will happen:</p>
            <ul className="list-disc list-inside text-muted-foreground space-y-0.5">
              <li>
                {source.emails.map((e) => e.email).join(", ")} will move to{" "}
                {selectedTarget.name}
              </li>
              <li>The Tharstern user record will be deleted</li>
              <li>Portfolio memberships will update on next sync cycle</li>
            </ul>
          </div>
        )}

        {/* Error */}
        {error && (
          <p
            role="alert"
            className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-md px-3 py-2"
          >
            {error}
          </p>
        )}

        {/* Actions */}
        <div className="flex justify-end gap-3 pt-2">
          <button
            type="button"
            onClick={onCancel}
            className="px-4 py-2 text-sm border border-input rounded-md hover:bg-muted transition-colors"
          >
            Cancel
          </button>
          <button
            type="button"
            disabled={!selectedTargetId || merging}
            onClick={() => selectedTargetId && onConfirm(selectedTargetId)}
            className="px-4 py-2 text-sm bg-primary text-primary-foreground rounded-md hover:bg-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {merging ? "Merging…" : "Merge"}
          </button>
        </div>
      </div>
    </div>
  );
}

export function AdminUsersPage() {
  const adapter = useDataAdapter();
  const [searchParams, setSearchParams] = useSearchParams();
  const roleFilter = searchParams.get("role") || undefined;

  const [users, setUsers] = useState<AdminUserItem[]>([]);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [firstLoad, setFirstLoad] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const debouncedSearch = useDebouncedValue(searchQuery, 300);
  const [mergeSource, setMergeSource] = useState<AdminUserItem | null>(null);
  const [merging, setMerging] = useState(false);
  const [mergeError, setMergeError] = useState<string | null>(null);

  // CSR account allocation — possible-leaver banner + mark-as-left/reinstate
  // (docs/features/csr-account-allocation/ 04_module_update_spec_users_module.md §9).
  const { user: currentUser } = useAuth();
  const [possibleLeavers, setPossibleLeavers] = useState<PossibleLeaverRow[]>(
    [],
  );
  const loadPossibleLeavers = useCallback(() => {
    adapter
      .getPossibleLeavers({ includeDismissed: false, pageSize: 50 })
      .then((res) => setPossibleLeavers(res.items));
  }, [adapter]);
  useEffect(loadPossibleLeavers, [loadPossibleLeavers]);

  const [markLeftTarget, setMarkLeftTarget] = useState<{
    userId: string;
    name: string;
    bookSize: number | null;
  } | null>(null);
  const [markingLeft, setMarkingLeft] = useState(false);
  const [reinstating, setReinstating] = useState<string | null>(null);
  const [dismissingSignal, setDismissingSignal] = useState<string | null>(null);

  const page = Math.max(1, parseInt(searchParams.get("page") ?? "1", 10) || 1);

  const setRoleFilter = useCallback(
    (next: string | undefined) => {
      setSearchParams(
        (prev) => {
          const params = new URLSearchParams(prev);
          if (!next || next === "all") {
            params.delete("role");
          } else {
            params.set("role", next);
          }
          params.delete("page");
          return params;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  const setPage = useCallback(
    (p: number) => {
      setSearchParams(
        (prev) => {
          const params = new URLSearchParams(prev);
          params.set("page", String(p));
          return params;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  const loadUsers = useCallback(() => {
    const opts: AdminUserListOptions = {
      page,
      pageSize: PAGE_SIZE,
      sort: "name",
    };
    if (roleFilter) opts.role = roleFilter;
    if (debouncedSearch) opts.q = debouncedSearch;

    adapter.getAdminUsers(opts).then((res) => {
      setUsers(res.items);
      setTotal(res.total);
      setTotalPages(res.totalPages);
      setFirstLoad(false);
    });
  }, [adapter, page, roleFilter, debouncedSearch]);

  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  const [entraUsers, setEntraUsers] = useState<AdminUserItem[]>([]);

  // Fetch all Entra users when the merge dialog opens, independent of
  // the main list's filters/pagination.
  useEffect(() => {
    if (!mergeSource) {
      setEntraUsers([]);
      return;
    }
    let cancelled = false;
    adapter
      .getAdminUsers({ pageSize: 200, hasLoggedIn: true, sort: "name" })
      .then((res) => {
        if (!cancelled) setEntraUsers(res.items);
      });
    return () => {
      cancelled = true;
    };
  }, [adapter, mergeSource]);

  const handleMergeConfirm = async (targetUserId: string) => {
    if (!mergeSource || merging) return;
    setMerging(true);
    setMergeError(null);
    try {
      await adapter.mergeUser(mergeSource.id, targetUserId);
      setMergeSource(null);
      loadUsers();
    } catch (err) {
      setMergeError(
        err instanceof ApiError && err.detail
          ? err.detail
          : "Merge failed. Please try again, or contact support if it persists.",
      );
    } finally {
      setMerging(false);
    }
  };

  async function confirmMarkLeft() {
    if (!markLeftTarget) return;
    setMarkingLeft(true);
    try {
      await adapter.markUserLeft(
        markLeftTarget.userId,
        currentTime().toISOString().slice(0, 10),
      );
      toast.success(`${markLeftTarget.name} marked as left`);
      setMarkLeftTarget(null);
      loadUsers();
      loadPossibleLeavers();
    } catch (err) {
      toast.error(
        err instanceof ApiError ? err.detail : "Could not mark as left",
      );
    } finally {
      setMarkingLeft(false);
    }
  }

  async function handleReinstate(u: AdminUserItem) {
    setReinstating(u.id);
    try {
      await adapter.reinstateUser(u.id);
      toast.success(`${u.name} reinstated`);
      loadUsers();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.detail : "Could not reinstate");
    } finally {
      setReinstating(null);
    }
  }

  async function handleDismissPossibleLeaver(row: PossibleLeaverRow) {
    setDismissingSignal(row.user_id);
    try {
      await adapter.dismissPossibleLeaver(row.user_id, row.signal);
      loadPossibleLeavers();
    } finally {
      setDismissingSignal(null);
    }
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-foreground flex items-center gap-2">
            <Users className="h-6 w-6" /> Manage users
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            {total} {total === 1 ? "user" : "users"} across all portfolios
          </p>
        </div>
      </div>

      {/* Possibly-left banner — Tharstern `Deleted` signal, unconfirmed.
          Product brief §16: signals are prompts, never automatic triggers. */}
      {possibleLeavers.length > 0 && (
        <Card className="p-4 border-amber-300 bg-amber-50 dark:border-amber-900/50 dark:bg-amber-900/20 space-y-3">
          <p className="text-sm font-medium text-amber-900 dark:text-amber-200 flex items-center gap-2">
            <AlertTriangle className="h-4 w-4" />
            Possibly left — deleted in Tharstern
          </p>
          <div className="space-y-2">
            {possibleLeavers.map((p) => (
              <div
                key={p.user_id}
                className="flex items-center justify-between gap-3 text-sm bg-background/60 rounded-md px-3 py-2"
              >
                <span>
                  <span className="font-medium">{p.user_name}</span>
                  {" — deleted in Tharstern — "}
                  {p.book_size} account{p.book_size === 1 ? "" : "s"} would move
                </span>
                <div className="flex items-center gap-2 shrink-0">
                  <Button
                    size="sm"
                    onClick={() =>
                      setMarkLeftTarget({
                        userId: p.user_id,
                        name: p.user_name,
                        bookSize: p.book_size,
                      })
                    }
                  >
                    Confirm they&apos;ve left
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={dismissingSignal === p.user_id}
                    onClick={() => handleDismissPossibleLeaver(p)}
                  >
                    Not a leaver
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* Filters */}
      <div className="flex items-center gap-4">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <input
            type="text"
            placeholder="Search by name or email..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-9 pr-3 py-2 border border-input rounded-md bg-background text-sm focus:outline-none focus:ring-2 focus:ring-ring"
          />
        </div>
        <select
          value={roleFilter || "all"}
          onChange={(e) =>
            setRoleFilter(e.target.value === "all" ? undefined : e.target.value)
          }
          className="border border-input rounded-md px-3 py-2 bg-background text-sm"
        >
          <option value="all">All roles</option>
          {BUSINESS_ROLES.map((role) => (
            <option key={role} value={role}>
              {roleLabel(role)}
            </option>
          ))}
        </select>
      </div>

      {/* User list */}
      <Card className="overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b bg-muted/50">
                <th className="text-left px-4 py-3 font-medium text-muted-foreground">
                  Name
                </th>
                <th className="text-left px-4 py-3 font-medium text-muted-foreground">
                  Email
                </th>
                <th className="text-left px-4 py-3 font-medium text-muted-foreground">
                  Role
                </th>
                <th className="text-left px-4 py-3 font-medium text-muted-foreground">
                  Identity
                </th>
                <th className="text-left px-4 py-3 font-medium text-muted-foreground">
                  Portfolios
                </th>
                <th className="text-right px-4 py-3 font-medium text-muted-foreground" />
              </tr>
            </thead>
            <tbody>
              {firstLoad ? (
                <tr>
                  <td
                    colSpan={6}
                    className="px-4 py-8 text-center text-muted-foreground"
                  >
                    Loading...
                  </td>
                </tr>
              ) : users.length === 0 ? (
                <tr>
                  <td
                    colSpan={6}
                    className="px-4 py-8 text-center text-muted-foreground"
                  >
                    No users found
                  </td>
                </tr>
              ) : (
                users.map((u) => (
                  <tr
                    key={u.id}
                    className="border-b last:border-b-0 hover:bg-muted/30"
                  >
                    <td className="px-4 py-3 font-medium">
                      <div className="flex items-center gap-1.5">
                        <span>{u.name || "(unnamed)"}</span>
                        {u.left_at && (
                          <Badge variant="neutral">
                            Left on {formatShortDate(u.left_at)}
                          </Badge>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1 text-muted-foreground">
                        <Mail className="h-3 w-3 flex-shrink-0" />
                        <span>{u.primary_email || "—"}</span>
                      </div>
                      {u.emails.length > 1 && (
                        <div className="mt-1 text-xs text-muted-foreground">
                          +{u.emails.length - 1} more
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <RoleBadge role={u.role} />
                    </td>
                    <td className="px-4 py-3">
                      <IdentityBadge hasLoggedIn={u.has_logged_in} />
                    </td>
                    <td className="px-4 py-3">
                      {u.portfolios.length === 0 ? (
                        <span className="text-muted-foreground">—</span>
                      ) : (
                        <div className="flex flex-wrap gap-1">
                          {u.portfolios.map((p) => (
                            <span
                              key={p.id}
                              className="inline-flex items-center px-2 py-0.5 rounded bg-muted text-xs"
                            >
                              <Shield className="h-3 w-3 mr-1" />
                              {p.name}
                            </span>
                          ))}
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="inline-flex items-center gap-2">
                        {!u.has_logged_in && (
                          <button
                            type="button"
                            onClick={() => {
                              setMergeSource(u);
                              setMergeError(null);
                            }}
                            className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium border border-input rounded-md hover:bg-muted transition-colors"
                          >
                            <Merge className="h-3 w-3" /> Merge
                          </button>
                        )}
                        {u.left_at ? (
                          <Button
                            size="sm"
                            variant="outline"
                            disabled={reinstating === u.id}
                            onClick={() => handleReinstate(u)}
                          >
                            <UserCheck className="h-3 w-3" />
                            {reinstating === u.id
                              ? "Reinstating…"
                              : "Reinstate"}
                          </Button>
                        ) : (
                          u.id !== currentUser?.id && (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() =>
                                setMarkLeftTarget({
                                  userId: u.id,
                                  name: u.name,
                                  bookSize: null,
                                })
                              }
                            >
                              <UserMinus className="h-3 w-3" />
                              Mark as left
                            </Button>
                          )
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </Card>

      {totalPages > 1 && (
        <ListPagination
          page={page}
          totalPages={totalPages}
          onPageChange={setPage}
        />
      )}

      {/* Merge dialog */}
      {mergeSource && (
        <MergeDialog
          source={mergeSource}
          entraUsers={entraUsers}
          merging={merging}
          error={mergeError}
          onConfirm={handleMergeConfirm}
          onCancel={() => {
            setMergeSource(null);
            setMergeError(null);
          }}
        />
      )}

      {/* Mark-as-left confirmation — product brief §16: a false "left"
          mass-moves a CSR's book, so the consequence is spelled out before
          confirming, not just the name. */}
      <Dialog
        open={markLeftTarget != null}
        onOpenChange={(open) => !open && setMarkLeftTarget(null)}
      >
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Mark as left</DialogTitle>
            <DialogDescription>
              {markLeftTarget?.name}
              {markLeftTarget?.bookSize != null
                ? ` — their ${markLeftTarget.bookSize} account${
                    markLeftTarget.bookSize === 1 ? "" : "s"
                  } will move to Needs a CSR, and they will lose access to One.`
                : " — their accounts will move to Needs a CSR, and they will lose access to One."}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setMarkLeftTarget(null)}>
              Cancel
            </Button>
            <Button disabled={markingLeft} onClick={confirmMarkLeft}>
              {markingLeft ? "Marking…" : "Mark as left"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
